/**
 * Mini-app TRIGGERS — file-event automations, stored in `_appsystem/triggers.json`
 * (the successor to `manifest.json`'s `app_hooks`; see
 * docs/plans/2026-06-30_MODE_B_SERVING_AND_AUTH.md §2).
 *
 * A trigger is `{ when, do }`:
 *   - `when` — a small MongoDB-like FILTER over the file-event document (fields
 *     below). The old `{ on, match }` (single event + glob) is just the trivial
 *     case of this. The editor builds the filter from a friendly rule UI; power
 *     users can edit the raw JSON.
 *   - `do`  — the action. Today `{ type: 'python', script }` (run a script in the
 *     sandbox as the app principal). A discriminated union so `{ type: 'agent', … }`
 *     (run an agent with the miniapp as linked workspace) slots in later.
 *
 * The filter runs IN-MEMORY against a `TriggerEventDoc` (built from the FileEvent
 * + the file's path within the app folder). Matching is fail-closed: an unknown
 * operator or a bad regex → no match, never a throw.
 */
import { z } from 'zod'
import { AppHookEventSchema } from './manifest.js'

// Reuse the canonical file-event enum (created/updated/moved/deleted/restored).
export const TriggerEventSchema = AppHookEventSchema
export type TriggerEvent = z.infer<typeof TriggerEventSchema>

/** Which subsystem a trigger listens to. `file` is the original; the rest ride
 *  the org bus (`:org` channel — role/member/team/invite). Adding a source =
 *  add its events to the dispatcher + a builder below; the filter/matcher are
 *  source-agnostic. */
export type TriggerSource = 'file' | 'member' | 'role' | 'team' | 'invite' | 'schedule'

// ─── The document a filter matches against ──────────────────────────
/** Flattened, filter-friendly view of ANY domain event. `source` discriminates;
 *  every field is optional except source/event/actor because each source only
 *  populates its own. The matcher just reads whatever keys the filter names. */
export interface TriggerEventDoc {
  source: TriggerSource
  event: string        // 'file:created' | 'org:member_added' | 'team:created' | 'org:role_changed' | …
  actor: string        // who caused it (email), '' if system
  // File source
  name?: string
  ext?: string
  path?: string
  isFolder?: boolean
  fileType?: string | null
  size?: number
  mimeType?: string | null
  // Org / member / role / team / invite sources
  email?: string        // affected member's email
  role?: string         // new role (member_added / role_changed / team member)
  previousRole?: string // prior role (role_changed)
  teamId?: string
  teamName?: string
}

/** Build the doc for a FILE event. Dependency-free (no import of FileEvent) so
 *  the matcher stays in @papan/types. `path` is the file's path WITHIN the app
 *  folder; `ext` is derived (lowercased, no dot). */
export function buildFileTriggerDoc(input: {
  event: TriggerEvent
  name: string
  path: string
  actor?: string
  isFolder?: boolean
  fileType?: string | null
  size?: number
  mimeType?: string | null
}): TriggerEventDoc {
  const name = input.name ?? ''
  const dot = name.lastIndexOf('.')
  const ext = dot > 0 ? name.slice(dot + 1).toLowerCase() : ''
  return {
    source: 'file',
    event: input.event,
    actor: input.actor ?? '',
    name,
    ext,
    path: input.path ?? '',
    isFolder: !!input.isFolder,
    fileType: input.fileType ?? null,
    size: input.size ?? 0,
    mimeType: input.mimeType ?? null,
  }
}

/** Build the doc for an ORG-bus event (member/role/team/invite). `event` is the
 *  raw bus type ('org:member_added', 'team:created', …); `source` is derived. */
export function buildOrgTriggerDoc(input: {
  event: string
  email?: string
  role?: string
  previousRole?: string
  teamId?: string
  teamName?: string
  actor?: string
}): TriggerEventDoc {
  const source: TriggerSource = input.event.startsWith('team:') ? 'team'
    : input.event === 'org:role_changed' ? 'role'
    : input.event === 'org:invite_changed' ? 'invite'
    : 'member'
  return {
    source,
    event: input.event,
    actor: input.actor ?? '',
    email: input.email ?? '',
    role: input.role,
    previousRole: input.previousRole,
    teamId: input.teamId,
    teamName: input.teamName,
  }
}

/** @deprecated use buildFileTriggerDoc. */
export const buildTriggerEventDoc = buildFileTriggerDoc

/** Build the doc for a SCHEDULE tick (time-fired, not event-fired). There is no
 *  actor — a schedule runs unattended as the app principal. `firedAt` is an ISO
 *  timestamp the script can read; `scheduleId` lets the script disambiguate when
 *  an app has several schedules. Passed to the runner in the same envelope shape
 *  as a file/org trigger so the runner path is uniform. */
export function buildScheduleTriggerDoc(input: { scheduleId: string; name?: string; firedAt?: string }): TriggerEventDoc & { scheduleId: string; firedAt?: string } {
  return {
    source: 'schedule',
    event: 'schedule:tick',
    actor: '',
    name: input.name,
    scheduleId: input.scheduleId,
    ...(input.firedAt ? { firedAt: input.firedAt } : {}),
  }
}

// ─── Filter schema (bounded MongoDB-like subset) ────────────────────
const MAX_STR_LEN = 512
const MAX_REGEX_LEN = 200        // tighter for $regex — see isSafeRegex
export const MAX_FILTER_DEPTH = 6
const FilterLeaf = z.union([z.string().max(MAX_STR_LEN), z.number(), z.boolean(), z.null()])

const OperatorObject = z.object({
  $eq: FilterLeaf.optional(),
  $ne: FilterLeaf.optional(),
  $in: z.array(FilterLeaf).max(50).optional(),
  $nin: z.array(FilterLeaf).max(50).optional(),
  // Plain string ops — no regex engine, so ZERO ReDoS. The builder emits these
  // for contains/starts-with/ends-with; $regex is only the explicit power-user op.
  $contains: z.string().max(MAX_STR_LEN).optional(),
  $startsWith: z.string().max(MAX_STR_LEN).optional(),
  $endsWith: z.string().max(MAX_STR_LEN).optional(),
  $regex: z.string().max(MAX_REGEX_LEN).optional(),
  $gt: z.number().optional(),
  $gte: z.number().optional(),
  $lt: z.number().optional(),
  $lte: z.number().optional(),
  $exists: z.boolean().optional(),
}).strict()

/**
 * Heuristic ReDoS guard. The matcher runs in the API event-dispatch path, so a
 * catastrophic-backtracking regex from a (semi-trusted) miniapp author could hang
 * the event loop. shared/types is isomorphic → a native RE2 isn't available here,
 * so we reject the classic exploit shape: nested quantifiers / star-height ≥ 2
 * (e.g. `(a+)+`, `(a*)*`, `(.*)+`). Not a full analyzer — pairs with the plain
 * string ops above (which need no regex) and a tight length cap. RE2 in the
 * server-side dispatcher is the gold-standard follow-up.
 */
export function isSafeRegex(src: unknown): boolean {
  if (typeof src !== 'string' || src.length > MAX_REGEX_LEN) return false
  if (/\([^()]*[+*{][^()]*\)[+*{]/.test(src)) return false // quantified group, then a quantifier
  try { new RegExp(src) } catch { return false }
  return true
}

/**
 * API-boundary safety check for a whole filter: rejects too-deep nesting and any
 * unsafe `$regex`. Returns an error message or null. (The matcher itself is also
 * fail-closed, but the API rejects at author time so it never gets stored.)
 */
export function filterSafetyIssue(filter: unknown, depth = 0): string | null {
  if (depth > MAX_FILTER_DEPTH) return 'Filter is nested too deeply'
  if (!filter || typeof filter !== 'object' || Array.isArray(filter)) return null
  for (const [key, val] of Object.entries(filter as Record<string, unknown>)) {
    if (key === '$and' || key === '$or') {
      if (Array.isArray(val)) for (const sub of val) { const e = filterSafetyIssue(sub, depth + 1); if (e) return e }
      continue
    }
    if (key === '$not') { const e = filterSafetyIssue(val, depth + 1); if (e) return e; continue }
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const rx = (val as Record<string, unknown>).$regex
      if (typeof rx === 'string' && !isSafeRegex(rx)) return `Unsafe or too-complex regex: ${rx.slice(0, 40)}`
    }
  }
  return null
}

const Condition = z.union([FilterLeaf, OperatorObject])

/** Recursive filter: field→condition, plus `$and`/`$or`/`$not` combinators.
 *  Bounded (regex length, array size, combinator fan-out); overall size is
 *  additionally capped at the API boundary. */
export const TriggerFilterSchema: z.ZodType<any> = z.lazy(() =>
  z.object({
    $and: z.array(TriggerFilterSchema).max(20).optional(),
    $or: z.array(TriggerFilterSchema).max(20).optional(),
    $not: TriggerFilterSchema.optional(),
  }).catchall(Condition),
)
export type TriggerFilter = Record<string, any>

// ─── Action schema (discriminated; python today) ────────────────────
export const TriggerActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('python'), script: z.string().min(1).max(512) }),
  // FUTURE: z.object({ type: z.literal('agent'), prompt: z.string(), ... })
])
export type TriggerAction = z.infer<typeof TriggerActionSchema>

export const TriggerSchema = z.object({
  /** Stable id (editor-generated). Keys the operational state — the fire
   *  counter — so it survives edits/reorders. Optional for back-compat with
   *  hand-written files (those just don't accrue counts until re-saved). */
  id: z.string().max(64).optional(),
  name: z.string().max(120).optional(),
  when: TriggerFilterSchema,
  do: TriggerActionSchema,
  /** Per-trigger run/pause. A paused trigger stays in the file but the
   *  dispatcher skips it. Default enabled. Operational (WRITE-level), not a
   *  security gate. */
  enabled: z.boolean().default(true),
})
export type Trigger = z.infer<typeof TriggerSchema>

// ─── Schedules (time-fired automation) ──────────────────────────────
/** Floor on schedule frequency — a runaway sub-minute schedule would hammer the
 *  sandbox pool + job queue. `everySeconds` is clamped to this; a cron finer than
 *  1/min is rejected at author time. */
export const MIN_SCHEDULE_SECONDS = 60

/** Cheap structural check for a standard 5-field cron (`m h dom mon dow`). The
 *  authoritative parse happens in the registrar (BullMQ's cron-parser); this just
 *  rejects obvious garbage at author time and blocks a 6-field (seconds) cron,
 *  which would allow sub-minute firing. */
// [min,max] per cron field: minute, hour, day-of-month, month, day-of-week (0-7, 7=Sun).
const CRON_FIELD_RANGES: ReadonlyArray<readonly [number, number]> = [[0, 59], [0, 23], [1, 31], [1, 12], [0, 7]]
// L-b: cron-parser (BullMQ's engine) accepts 3-letter names in the month (`JAN`..`DEC`) and
// day-of-week (`SUN`..`SAT`) fields, incl. inside ranges (`MON-FRI`). Map a name token to its
// number so the validator doesn't reject a perfectly runnable schedule. Field-scoped: names are
// only meaningful in field index 3 (month) / 4 (dow); a name in any other field stays invalid.
const CRON_MONTH_NAMES: Record<string, number> = { JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6, JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12 }
const CRON_DOW_NAMES: Record<string, number> = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 }
/** Resolve a cron bound token to a number: a plain integer, or (in month/dow fields) a name. */
function cronBoundValue(token: string, fieldIndex: number): number | null {
  if (/^[0-9]+$/.test(token)) return Number(token)
  const names = fieldIndex === 3 ? CRON_MONTH_NAMES : fieldIndex === 4 ? CRON_DOW_NAMES : null
  const v = names?.[token.toUpperCase()]
  return v === undefined ? null : v
}

/**
 * RANGE-AWARE 5-field cron validation. A char-class-only check (`/^[0-9*,\-/]+$/`) passes
 * out-of-range values like `0 25 * * *` (hour 25) — which then throws INSIDE BullMQ's repeat
 * strategy (cron-parser, outside its try/catch), silently killing that app's later schedules.
 * Validate each field's numbers against its real range so the schema/PUT rejects it early with
 * a user-visible error. Supports `*`, `,` lists, `-` ranges, and `/` steps.
 */
export function isPlausibleCron(src: unknown): boolean {
  if (typeof src !== 'string') return false
  const parts = src.trim().split(/\s+/)
  if (parts.length !== 5) return false // exactly 5 fields — no seconds field
  return parts.every((field, i) => {
    const [lo, hi] = CRON_FIELD_RANGES[i]
    return field.split(',').every((item) => {
      if (item === '') return false
      const [range, step] = item.split('/')
      if (step !== undefined && (!/^[0-9]+$/.test(step) || Number(step) < 1)) return false // step is a POSITIVE int (reject `*/0`)
      if (range === '*') return true
      const bounds = range.split('-')
      if (bounds.length > 2) return false // at most `lo-hi`
      const nums = bounds.map((b) => cronBoundValue(b, i))
      if (!nums.every((n) => n !== null && n >= lo && n <= hi)) return false
      // Reject a REVERSED range (`17-9`) — bounds are in [lo,hi] but the pair is inverted, which
      // most cron parsers treat as empty/never-match (a silent no-fire schedule). Require start ≤ end.
      return nums.length < 2 || (nums[0] as number) <= (nums[1] as number)
    })
  })
}

/** The time zone a `cron` schedule without `tz` runs in. Explicit on purpose: before `tz`
 *  existed the cron ran on the HOST's clock, which is UTC on every server — so an absent
 *  `tz` keeps meaning UTC, and no longer depends on how a host is configured. */
export const SCHEDULE_DEFAULT_TZ = 'UTC'

/** A time zone name the runtime can evaluate — an IANA name like `Asia/Kuala_Lumpur`, or
 *  `UTC`. The same `Intl` database BullMQ's cron engine (cron-parser → luxon) reads, so a
 *  name accepted here is one the scheduler accepts. */
export function isValidTimeZone(tz: unknown): tz is string {
  if (typeof tz !== 'string' || tz.length === 0 || tz.length > 64) return false
  // A NAME (`Area/City`, `UTC`, `Etc/GMT+8`) — never a raw offset like `+08:00`, which newer
  // Intl engines accept but the scheduler's zone handling may read differently.
  if (!/^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+)*$/.test(tz)) return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

/** The zone a schedule's cron is evaluated in. */
export function scheduleTimeZone(s: { tz?: string }): string {
  return s.tz ?? SCHEDULE_DEFAULT_TZ
}

/**
 * The pattern + zone as one line — "cron \"0 9 * * *\" in Asia/Kuala_Lumpur". What a person
 * approves (the consent label) and sees listed: the exact pattern (paraphrasing a cron is
 * where wording drifts from the engine) and ALWAYS its zone; the UI adds the next run times
 * (`nextCronRuns`) on the viewer's own clock. Total over a hand-edited `triggers.json`.
 */
export function describeCronSchedule(cron: unknown, tz: unknown): string {
  // Only an ABSENT zone means UTC — `""` (or any other set value) is a zone the scheduler must
  // read, and says so if it can't (the registrar skips it; showing "UTC" would be a lie).
  const zone = tz == null ? SCHEDULE_DEFAULT_TZ : typeof tz === 'string' ? tz : String(tz)
  const pattern = typeof cron === 'string' ? cron.trim() : String(cron)
  return `cron "${pattern}" in ${isValidTimeZone(zone) ? zone : `unrecognised time zone "${zone}"`}`
}

/**
 * A time-fired automation. Exactly ONE of `cron` (a 5-field cron expression) or
 * `everySeconds` (a fixed interval, clamped to ≥ MIN_SCHEDULE_SECONDS) must be
 * set. `tz` (only with `cron`) is the IANA zone the cron's clock times mean; absent
 * = UTC (`SCHEDULE_DEFAULT_TZ`). `do` is the same action union as a trigger (python
 * today). Unattended: runs as the app principal with no actor. `id` is stable — it
 * keys the BullMQ job-scheduler so edits reconcile instead of duplicating.
 */
export const ScheduleSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().max(120).optional(),
  cron: z.string().max(120).optional(),
  // ANY JSON value parses (a hand-typed `null`, number, or overlong string must not fail the
  // whole file — see scheduleTimingIssue): a non-string or overlong value becomes a readable
  // marker that isValidTimeZone rejects, so it takes the same "unregistered + flagged" path.
  tz: z.preprocess(
    (v) => (v === undefined ? undefined : typeof v === 'string' && v.length <= 64 ? v : `invalid: ${JSON.stringify(v)?.slice(0, 40) ?? String(v)}`),
    z.string().optional(),
  ),
  everySeconds: z.number().int().min(MIN_SCHEDULE_SECONDS).max(60 * 60 * 24 * 31).optional(),
  do: TriggerActionSchema,
  enabled: z.boolean().default(true),
})
  .refine((s) => (s.cron == null) !== (s.everySeconds == null), {
    message: 'a schedule needs exactly one of `cron` or `everySeconds`',
  })
  .refine((s) => s.cron == null || isPlausibleCron(s.cron), {
    message: 'invalid cron — use 5 fields (m h dom mon dow), no seconds field',
  })
export type Schedule = z.infer<typeof ScheduleSchema>

/** A schedule's timing as one comparable key (cron | tz | everySeconds). */
export function scheduleTimingKey(s: { cron?: string; tz?: string; everySeconds?: number }): string {
  return `${s.cron ?? ''}|${s.tz ?? ''}|${s.everySeconds ?? ''}`
}

/**
 * The `tz` problems a WRITE must refuse: only on schedules whose timing the write sets or
 * changes (by id, against the stored file). Every writer saves the WHOLE file, so refusing an
 * UNCHANGED bad one (hand-edited) would block every unrelated save — "Pause all" and any agent
 * trigger edit included; that one stays unregistered and flagged until someone fixes it. One
 * rule for the settings API and `build_pdapp`.
 */
export function scheduleWriteTimingIssues(
  next: ReadonlyArray<{ id: string; name?: string; cron?: string; tz?: string; everySeconds?: number }>,
  stored: ReadonlyArray<{ id?: string; cron?: string; tz?: string; everySeconds?: number }>,
): string[] {
  const before = new Map(stored.map((x) => [x.id, scheduleTimingKey(x)]))
  const out: string[] = []
  for (const s of next) {
    const issue = scheduleTimingIssue(s)
    if (issue && before.get(s.id) !== scheduleTimingKey(s)) out.push(`schedule "${s.name ?? s.id}": ${issue}`)
  }
  return out
}

/**
 * A schedule's `tz` problem, or null. Deliberately NOT a schema refine: `triggers.json` is
 * parsed WHOLE by several readers (the worker's run gate, the settings page, `build_pdapp`),
 * so one hand-typed bad zone failing the schema would silently disable — and on the next
 * save, erase — every schedule in the app. Instead the file parses, the registrar refuses to
 * register that one schedule (never guessing a zone), the run gate refuses it, the UI says
 * "unrecognised time zone", and writes that set or change it are refused (`scheduleWriteTimingIssues`).
 */
export function scheduleTimingIssue(s: { cron?: string; everySeconds?: number; tz?: string }): string | null {
  if (s.tz == null) return null
  if (s.cron == null) return '`tz` applies to a `cron` schedule only (an `everySeconds` interval has no clock time)'
  if (!isValidTimeZone(s.tz)) return `invalid \`tz\` "${s.tz}" — use an IANA time zone name like "Asia/Kuala_Lumpur" or "UTC"`
  return null
}

/** The `_appsystem/triggers.json` file shape. */
export const TriggersFileSchema = z.object({
  /** Pause-all for FILE/ORG TRIGGERS only — when true the dispatcher fires no
   *  event-triggers for this app. Does NOT touch schedules (they keep running):
   *  a schedule is unattended clockwork, not a reaction to a user event, so the
   *  two pause independently (`schedulesPaused` is the schedules' own switch). */
  paused: z.boolean().default(false),
  triggers: z.array(TriggerSchema).max(64).default([]),
  /** Time-fired automations (cron / interval). Capped like triggers. */
  schedules: z.array(ScheduleSchema).max(32).default([]),
  /** Pause-all for SCHEDULES only — when true, no schedule is registered/fires.
   *  Independent of `paused` above. Per-schedule `enabled` still applies. */
  schedulesPaused: z.boolean().default(false),
})
export type TriggersFile = z.infer<typeof TriggersFileSchema>

// ─── The matcher ────────────────────────────────────────────────────
function matchOne(value: unknown, cond: unknown): boolean {
  // Primitive/array/null condition → implicit equality.
  if (cond === null || typeof cond !== 'object' || Array.isArray(cond)) {
    return value === cond
  }
  const ops = Object.entries(cond as Record<string, unknown>)
  // An empty operator object (`{ field: {} }`) has no constraints — fail-closed
  // (match NOTHING) rather than falling through the zero-iteration loop to `true`,
  // which would make it match every event. Only reachable via hand-edited JSON.
  if (ops.length === 0) return false
  for (const [op, operand] of ops) {
    switch (op) {
      case '$eq': if (value !== operand) return false; break
      case '$ne': if (value === operand) return false; break
      case '$in': if (!Array.isArray(operand) || !operand.includes(value as never)) return false; break
      case '$nin': if (Array.isArray(operand) && operand.includes(value as never)) return false; break
      case '$exists': {
        const exists = value !== undefined && value !== null
        if (exists !== Boolean(operand)) return false
        break
      }
      case '$contains': if (typeof value !== 'string' || typeof operand !== 'string' || !value.includes(operand)) return false; break
      case '$startsWith': if (typeof value !== 'string' || typeof operand !== 'string' || !value.startsWith(operand)) return false; break
      case '$endsWith': if (typeof value !== 'string' || typeof operand !== 'string' || !value.endsWith(operand)) return false; break
      case '$regex': {
        // Fail-closed on an unsafe/invalid pattern — never run catastrophic regex.
        if (typeof value !== 'string' || !isSafeRegex(operand)) return false
        let re: RegExp
        try { re = new RegExp(operand as string) } catch { return false }
        if (!re.test(value)) return false
        break
      }
      case '$gt': if (!(typeof value === 'number' && value > (operand as number))) return false; break
      case '$gte': if (!(typeof value === 'number' && value >= (operand as number))) return false; break
      case '$lt': if (!(typeof value === 'number' && value < (operand as number))) return false; break
      case '$lte': if (!(typeof value === 'number' && value <= (operand as number))) return false; break
      default: return false // unknown operator → fail-closed
    }
  }
  return true
}

/**
 * Does `doc` satisfy `filter`? Empty/absent filter matches everything. Top-level
 * keys are ANDed; `$and`/`$or`/`$not` combine sub-filters. Fail-closed on
 * anything malformed (never throws).
 */
export function matchesTriggerFilter(doc: TriggerEventDoc, filter: unknown, depth = 0): boolean {
  if (depth > MAX_FILTER_DEPTH) return false // fail-closed on over-deep nesting
  if (!filter || typeof filter !== 'object' || Array.isArray(filter)) return true
  for (const [key, val] of Object.entries(filter as Record<string, unknown>)) {
    if (key === '$and') {
      if (!Array.isArray(val) || !val.every((sub) => matchesTriggerFilter(doc, sub, depth + 1))) return false
      continue
    }
    if (key === '$or') {
      if (!Array.isArray(val) || !val.some((sub) => matchesTriggerFilter(doc, sub, depth + 1))) return false
      continue
    }
    if (key === '$not') {
      if (matchesTriggerFilter(doc, val, depth + 1)) return false
      continue
    }
    if (!matchOne((doc as unknown as Record<string, unknown>)[key], val)) return false
  }
  return true
}
